/**
 * Exchange CSV timestamps: explicit UTC, fixed offset, or IANA local wall-clock.
 * Avoids treating exchange-local times as UTC (Bitvavo, Bitpanda, etc.).
 */

/** @typedef {'utc'|'iana'|'fixed_offset'|'unix'|'auto'} TimeMode */

/**
 * Default policy per exchange when column does not state a timezone.
 * @type {Record<string, { mode: TimeMode, timeZone?: string, offsetMinutes?: number }>}
 */
export const EXCHANGE_TIME_DEFAULTS = {
  bitvavo: { mode: 'iana', timeZone: 'Europe/Amsterdam' },
  bitpanda: { mode: 'iana', timeZone: 'Europe/Vienna' },
  binance: { mode: 'utc' },
  binance_us: { mode: 'utc' },
  kraken: { mode: 'unix' },
  coinbase: { mode: 'utc' },
  bybit: { mode: 'utc' },
  crypto_com: { mode: 'utc' },
  gateio: { mode: 'utc' },
  mexc: { mode: 'utc' },
  bitget: { mode: 'utc' },
  okx: { mode: 'utc' },
  huobi: { mode: 'iana', timeZone: 'Asia/Shanghai' },
  upbit: { mode: 'iana', timeZone: 'Asia/Seoul' },
};

const UTC_SUFFIX_RE = /\s*(?:UTC|GMT|Z)\s*$/i;
const EXPLICIT_UTC_COL_RE = /\(utc(?:\+0)?\)|utc\)|timestamp\s*\(utc\)|date\s*\(utc\)/i;
const FIXED_OFFSET_COL_RE = /\(utc\+(\d+)\)|utc\+(\d+)/i;

/**
 * @param {Record<string, unknown>} row
 * @returns {{ raw: unknown, mode: TimeMode, offsetMinutes?: number, timeZone?: string, column?: string }}
 */
export function pickBinanceTimestamp(row) {
  const ordered = [
    ['Date(UTC+0)', { mode: 'utc' }],
    ['Date(UTC)', { mode: 'utc' }],
    ['Date(UTC+1)', { mode: 'fixed_offset', offsetMinutes: 60 }],
    ['Time', { mode: 'utc' }],
    ['Date', { mode: 'utc' }],
    ['Timestamp', { mode: 'utc' }],
    ['Datum', { mode: 'utc' }],
    ['Zeit', { mode: 'utc' }],
  ];

  for (const [column, policy] of ordered) {
    const raw = row?.[column];
    if (raw != null && String(raw).trim() !== '') {
      return { raw, column, ...policy };
    }
  }

  return { raw: null, mode: 'utc', column: null };
}

/**
 * Infer policy from CSV column header name.
 * @param {string} [columnKey]
 */
export function policyFromColumn(columnKey) {
  if (!columnKey) return null;
  const k = String(columnKey).trim();
  if (UTC_SUFFIX_RE.test(k) || EXPLICIT_UTC_COL_RE.test(k)) {
    return { mode: 'utc', column: k };
  }
  const m = k.match(FIXED_OFFSET_COL_RE);
  if (m) {
    const hours = Number(m[1] ?? m[2]);
    if (Number.isFinite(hours)) {
      return { mode: 'fixed_offset', offsetMinutes: hours * 60, column: k };
    }
  }
  return null;
}

/**
 * @param {string} exchangeId
 * @param {{ mode?: TimeMode, timeZone?: string, offsetMinutes?: number, column?: string }} [overrides]
 */
export function resolveExchangeTimePolicy(exchangeId, overrides = {}) {
  if (overrides.mode) return { exchange: exchangeId, ...overrides };
  const base = EXCHANGE_TIME_DEFAULTS[exchangeId] || { mode: 'utc' };
  if (overrides.column) {
    const fromCol = policyFromColumn(overrides.column);
    if (fromCol) return { exchange: exchangeId, ...fromCol };
  }
  return { exchange: exchangeId, ...base };
}

/**
 * Convert wall-clock components in IANA zone to UTC epoch ms (DST-safe).
 */
export function wallClockToUtcMs(parts, timeZone) {
  const { year, month, day, hour, minute, second = 0 } = parts;
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second);
  const offset = getTimezoneOffsetMs(timeZone, utcGuess);
  return utcGuess - offset;
}

function getTimezoneOffsetMs(timeZone, utcMs) {
  const z = getZonedParts(utcMs, timeZone);
  const zonedAsUtc = Date.UTC(z.year, z.month - 1, z.day, z.hour, z.minute, z.second);
  return zonedAsUtc - utcMs;
}

function getZonedParts(ms, timeZone) {
  const s = new Intl.DateTimeFormat('sv-SE', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date(ms));
  const [datePart, timePart] = s.split(' ');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute, second] = timePart.split(':').map(Number);
  return { year, month, day, hour, minute, second };
}

function parseWallClockParts(s) {
  const str = String(s ?? '').trim();
  if (!str) return null;

  if (UTC_SUFFIX_RE.test(str)) {
    const stripped = str.replace(UTC_SUFFIX_RE, '').trim();
    const p = parseWallClockParts(stripped);
    return p ? { ...p, explicitUtc: true } : null;
  }

  const iso = str.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/
  );
  if (iso) {
    const [, y, mo, d, h, mi, se = '0'] = iso;
    return parts(+y, +mo, +d, +h, +mi, +se);
  }

  const de = str.match(/^(\d{2})\.(\d{2})\.(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (de) {
    const [, d, mo, y, h = '0', mi = '0', se = '0'] = de;
    return parts(+y, +mo, +d, +h, +mi, +se);
  }

  const fr = str.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (fr) {
    const [, d, mo, y, h = '0', mi = '0', se = '0'] = fr;
    return parts(+y, +mo, +d, +h, +mi, +se);
  }

  const cn = str.match(/^(\d{4})年(\d{1,2})月(\d{1,2})日(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (cn) {
    const [, y, mo, d, h = '0', mi = '0', se = '0'] = cn;
    return parts(+y, +mo, +d, +h, +mi, +se);
  }

  const jp = str.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (jp) {
    const [, y, mo, d, h = '0', mi = '0', se = '0'] = jp;
    return parts(+y, +mo, +d, +h, +mi, +se);
  }

  return null;

  function parts(year, month, day, hour, minute, second) {
    if (![year, month, day, hour, minute, second].every(Number.isFinite)) return null;
    return { year, month, day, hour, minute, second, explicitUtc: false };
  }
}

function parseUnixValue(v) {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v > 1e12 ? Math.floor(v) : Math.floor(v * 1000);
  }
  const s = String(v ?? '').trim();
  if (!s) return null;
  if (/^\d+(\.\d+)?$/.test(s)) {
    const n = Number(s);
    if (!Number.isFinite(n)) return null;
    return n > 1e12 ? Math.floor(n) : Math.floor(n * 1000);
  }
  return null;
}

function formatInterpretation(policy) {
  if (policy.mode === 'iana' && policy.timeZone) return `iana:${policy.timeZone}`;
  if (policy.mode === 'fixed_offset' && Number.isFinite(policy.offsetMinutes)) {
    return `fixed_offset:+${policy.offsetMinutes}m`;
  }
  if (policy.mode === 'unix') return 'unix';
  return 'utc';
}

/**
 * @param {unknown} value
 * @param {{ exchange?: string, mode?: TimeMode, timeZone?: string, offsetMinutes?: number, column?: string }} [opts]
 * @returns {{ iso: string|null, epochSec: number|undefined, interpretation: string }}
 */
export function parseExchangeInstant(value, opts = {}) {
  const empty = { iso: null, epochSec: undefined, interpretation: 'invalid' };

  if (value == null || value === '') return empty;

  const policy = resolveExchangeTimePolicy(opts.exchange || 'unknown', opts);
  const interpretation = formatInterpretation(policy);

  const unixMs = parseUnixValue(value);
  if (unixMs != null && (policy.mode === 'unix' || policy.exchange === 'kraken')) {
    const d = new Date(unixMs);
    if (!Number.isFinite(d.getTime())) return empty;
    return { iso: d.toISOString(), epochSec: Math.floor(unixMs / 1000), interpretation: 'unix' };
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value > 1e12 ? value : value * 1000;
    const d = new Date(ms);
    if (!Number.isFinite(d.getTime())) return empty;
    return { iso: d.toISOString(), epochSec: Math.floor(ms / 1000), interpretation: 'unix' };
  }

  const raw = String(value).trim();
  if (!raw) return empty;

  if (/[zZ]$|[+-]\d{2}(?::?\d{2})?\s*$/i.test(raw)) {
    const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
    const ms = Date.parse(normalized);
    if (Number.isFinite(ms)) {
      const d = new Date(ms);
      return {
        iso: d.toISOString(),
        epochSec: Math.floor(ms / 1000),
        interpretation: 'explicit_tz',
      };
    }
  }

  const wall = parseWallClockParts(raw);
  if (!wall) return empty;

  if (wall.explicitUtc || policy.mode === 'utc') {
    const ms = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
    const d = new Date(ms);
    if (!Number.isFinite(d.getTime())) return empty;
    return { iso: d.toISOString(), epochSec: Math.floor(ms / 1000), interpretation: 'utc' };
  }

  if (policy.mode === 'fixed_offset' && Number.isFinite(policy.offsetMinutes)) {
    const localMs = Date.UTC(
      wall.year,
      wall.month - 1,
      wall.day,
      wall.hour,
      wall.minute,
      wall.second
    );
    const ms = localMs - policy.offsetMinutes * 60 * 1000;
    const d = new Date(ms);
    if (!Number.isFinite(d.getTime())) return empty;
    return {
      iso: d.toISOString(),
      epochSec: Math.floor(ms / 1000),
      interpretation: formatInterpretation(policy),
    };
  }

  if (policy.mode === 'iana' && policy.timeZone) {
    const ms = wallClockToUtcMs(wall, policy.timeZone);
    const d = new Date(ms);
    if (!Number.isFinite(d.getTime())) return empty;
    return {
      iso: d.toISOString(),
      epochSec: Math.floor(ms / 1000),
      interpretation: formatInterpretation(policy),
    };
  }

  const ms = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
  const d = new Date(ms);
  return { iso: d.toISOString(), epochSec: Math.floor(ms / 1000), interpretation: 'utc' };
}

/**
 * Binance row → normalized instant with correct UTC/UTC+1 column semantics.
 * @param {Record<string, unknown>} row
 * @param {(v: unknown) => unknown} [normalizeDate]
 */
export function parseBinanceInstant(row, normalizeDate = (v) => v) {
  const picked = pickBinanceTimestamp(row);
  const raw = picked.raw == null ? null : normalizeDate(picked.raw);
  const colPolicy = picked.column ? policyFromColumn(picked.column) : null;
  const mode = colPolicy?.mode || picked.mode;
  return parseExchangeInstant(raw, {
    exchange: 'binance',
    mode,
    offsetMinutes: colPolicy?.offsetMinutes ?? picked.offsetMinutes,
    column: picked.column,
  });
}
